import type { AccessId, AccessRole, OrgAddress, OrgAclEntry, OrgPrefs, OrgRole } from '@/types/org';
import { navigate } from '@/utils/navigate';
import { orgService } from '@/services/org-service';
import { ticketService } from '@/services/ticket-service';
import './nu-user-menu';
import { applyDevWatermark } from '@/services/dev-watermark';

function genId(): string {
  return 'nu_' + Math.random().toString(36).slice(2, 9);
}

class NuOrg extends HTMLElement {
  protected readonly root: ShadowRoot;
  private _closeMenu: (() => void) | null = null;

  private prefs: OrgPrefs = {
    skipCheckoutConfirmations: false,
  };
  private _template = '';
  private _templateVisible = false;
  private _entitlements: string[] = [];

  private get _canManageSettings() { return this._entitlements.includes('ac/org/settings/manage'); }
  private get _canManageAcl()      { return this._entitlements.includes('ac/org/acl/manage'); }
  private acl: OrgAclEntry[] = [];
  private _aclEditingIds = new Set<number>();
  private _aclNewIndices = new Set<number>();
  private _aclSnapshots = new Map<number, OrgAclEntry>();
  private _aclSaving = new Set<number>();
  private _aclErrors = new Map<number, string>();
  private _aclAbort: AbortController | null = null;
  private accessIds: AccessId[] = [];
  private _editingIds = new Set<number>();
  private _newIndices = new Set<number>();
  private _snapshots = new Map<number, AccessId>();
  private _hideDisabled = true;
  private _accessSaving = new Set<number>();
  private _accessErrors = new Map<number, string>();
  private _accessIdsAbort: AbortController | null = null;
  private _initialized = false;
  private _loading = false;
  private _loadError = '';
  private _saving = false;
  private _orgNameError = '';
  private _addressError = '';

  private _orgName = '';
  private _orgNameEditing = false;
  private _orgNameSnapshot = '';
  private _storeNumber = '';
  private _orgAddress: OrgAddress = { street1: '', city: '', state: '', postalCode: '' };
  private _orgAddressEditing = false;
  private _orgAddressSnapshot: OrgAddress = { street1: '', city: '', state: '', postalCode: '' };
  private _skipCheckoutEditing = false;
  private _skipCheckoutSnapshot = false;
  private _skipCheckoutError = '';
  private _templateEditing = false;
  private _templateSnapshot = '';
  private _templateError = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id']; }
  get orgId() { return this.getAttribute('org-id') ?? ''; }
  get isNew() { return !this.getAttribute('org-id'); }

  connectedCallback() {
    if (!this._initialized) {
      this._initialized = true;
      if (this.isNew) {
        this._orgNameEditing = true;
        this._orgAddressEditing = true;
        this._skipCheckoutEditing = true;
      } else {
        this._loading = true;
        this._loadOrg();
      }
    }
    this._render();
    applyDevWatermark(this.root);
  }

  private _setSaving(saving: boolean) {
    this._saving = saving;
    const overlay = this.root.querySelector<HTMLElement>('#page-overlay');
    if (overlay) overlay.style.display = saving ? 'flex' : 'none';
  }

  private _currentOrgReq() {
    return {
      name: this._orgName,
      storeNumber: this._storeNumber || undefined,
      address: { ...this._orgAddress },
      prefs: {
        skipCheckoutConfirmations: this.prefs.skipCheckoutConfirmations,
      },
    };
  }

  private async _loadOrg() {
    try {
      const [orgResult, tmtResult, entsResult] = await Promise.allSettled([
        orgService.getOrgById(this.orgId),
        ticketService.getTextMessageTemplate(this.orgId),
        orgService.getOrgEntitlements(this.orgId),
      ]);
      if (orgResult.status === 'rejected') {
        throw orgResult.reason;
      }
      const org = orgResult.value;
      this._orgName = org.name;
      this._storeNumber = org.storeNumber ?? '';
      this._orgAddress = { ...org.address };
      this.prefs = { ...org.prefs };
      this.acl = org.acl.map(e => ({ ...e, roles: [...e.roles] }));
      this.accessIds = org.accessIds.map(a => ({ ...a, roles: [...a.roles] }));
      if (tmtResult.status === 'fulfilled') {
        this._template = tmtResult.value.template ?? '';
        this._templateVisible = true;
      }
      if (entsResult.status === 'fulfilled') {
        this._entitlements = entsResult.value;
      }
      this._loading = false;
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load organization.';
      this._loading = false;
    }
    this._render();
  }

  disconnectedCallback() {
    if (this._closeMenu) document.removeEventListener('click', this._closeMenu);
  }

  private _render() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>notify-u</span>
          <div class="header-actions">
            <button class="btn-back" id="back-btn">← Back</button>
            <nu-user-menu></nu-user-menu>
          </div>
        </header>

        ${this.isNew ? `
          <div class="org-hero">
            <span class="org-initial">${this._orgName || '+'}</span>
          </div>

          <div class="body">
            ${this._orgNameHTML()}

            <div class="field-group">
              <div class="field-label mono">Store Number <span class="field-optional">(optional)</span></div>
              <input class="field-input" id="store-number-input" type="text"
                     value="${this._storeNumber}" placeholder="e.g. 42">
            </div>

            ${this._addressSectionHTML()}

            <section class="section">
              <div class="section-label mono">Preferences</div>
              ${this._skipCheckoutCardHTML()}
            </section>

            <div class="new-org-actions">
              <button class="btn-done btn-create-org" id="create-org-btn">Create Organization</button>
              <button class="btn-card-action" id="cancel-org-btn">Cancel</button>
            </div>
            <p class="new-org-error" id="new-org-error"></p>
          </div>
        ` : this._loading ? `
          <div class="body">
            <p class="loading-msg mono">Loading…</p>
          </div>
        ` : !this._loadError ? `
          <div class="org-hero">
            <span class="org-initial">${this._orgName}</span>
          </div>

          <div class="body">
            ${this._orgNameHTML()}
            ${this._storeNumber ? `<div class="store-num mono">Store #${this._storeNumber}</div>` : ''}

            ${this._addressSectionHTML()}

            <section class="section">
              <div class="section-label mono">Preferences</div>
              ${this._skipCheckoutCardHTML()}
              ${this._templateCardHTML()}
            </section>

            <section class="section">
              <div class="section-header">
                <div class="section-label mono">Access Control</div>
                ${this._canManageAcl ? `<button class="btn-create" id="acl-add-btn">+ Add Member</button>` : ''}
              </div>
              <div id="acl-list">${this._aclHTML()}</div>
            </section>

            <section class="section">
              <div class="section-header">
                <div class="section-label mono">Anonymous Access Tokens</div>
                <div class="section-menu-wrap">
                  <button class="menu-btn" id="section-menu-btn" aria-label="Access ID options">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <circle cx="8" cy="2" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="14" r="1.5"/>
                    </svg>
                  </button>
                  <div class="section-dropdown" id="section-dropdown" style="display:none">
                    ${this._canManageSettings ? `<button class="section-dropdown-item" id="create-access-id">+ New Token</button>` : ''}
                    <button class="section-dropdown-item" id="print-qr-codes">Print QR Codes</button>
                    <button class="section-dropdown-item" id="sub-qr-codes">Subscription Stickers</button>
                    <div class="section-dropdown-divider"></div>
                    <button class="section-dropdown-item" id="toggle-hide-disabled">${this._hideDisabled ? 'Show Disabled' : 'Hide Disabled'}</button>
                  </div>
                </div>
              </div>
              <div id="access-ids-list">
                ${this._accessIdsHTML()}
              </div>
            </section>
          </div>
        ` : `
          <div class="body">
            <p class="not-found">${this._loadError || 'Organization not found.'}</p>
          </div>
        `}
      </div>
      <div id="page-overlay" style="display:none">
        <div class="spinner"></div>
      </div>
    `;

    this.root.querySelector('#back-btn')!.addEventListener('click', () => navigate('/app'));
    this._closeMenu = () => {
      this.root.querySelector<HTMLElement>('#section-dropdown')?.style.setProperty('display', 'none');
    };
    document.addEventListener('click', this._closeMenu);

    if (this.isNew) {
      this._bindNewMode();
      this._bindSkipCheckoutCard();
      return;
    }

    if (this._loading || this._loadError) return;

    this._bindOrgName();
    this._bindAddressSection();
    this._bindSkipCheckoutCard();
    this._bindTemplateCard();

    this.root.querySelector('#acl-add-btn')!.addEventListener('click', () => {
      // shift all existing tracked indices up by 1 before unshifting into the array
      this._aclEditingIds = new Set([...this._aclEditingIds].map(i => i + 1));
      this._aclNewIndices = new Set([...this._aclNewIndices].map(i => i + 1));
      const shiftedSnapshots = new Map<number, OrgAclEntry>();
      for (const [k, v] of this._aclSnapshots) shiftedSnapshots.set(k + 1, v);
      this._aclSnapshots = shiftedSnapshots;
      this.acl.unshift({ email: '', roles: ['CHECK-IN', 'CHECK-OUT'] });
      this._aclEditingIds.add(0);
      this._aclNewIndices.add(0);
      this._refreshAcl();
    });
    this._bindAcl();

    const sectionMenuBtn = this.root.querySelector<HTMLElement>('#section-menu-btn')!;
    const sectionDropdown = this.root.querySelector<HTMLElement>('#section-dropdown')!;
    sectionMenuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      sectionDropdown.style.display = sectionDropdown.style.display === 'none' ? 'block' : 'none';
    });
    sectionDropdown.addEventListener('click', (e) => e.stopPropagation());

    this.root.querySelector('#print-qr-codes')!.addEventListener('click', () => {
      sectionDropdown.style.display = 'none';
      this._printQrCodes();
    });

    this.root.querySelector('#sub-qr-codes')!.addEventListener('click', () => {
      sectionDropdown.style.display = 'none';
      navigate(`/app/org/${this.orgId}/sub-qr`);
    });

    this.root.querySelector('#create-access-id')!.addEventListener('click', () => {
      sectionDropdown.style.display = 'none';
      const newIdx = this.accessIds.length;
      this.accessIds.push({ id: genId(), roles: ['CHECK-IN', 'CHECK-OUT'], createdTS: new Date().toISOString() });
      this._editingIds.add(newIdx);
      this._newIndices.add(newIdx);
      this._refreshAccessIds();
    });

    this.root.querySelector('#toggle-hide-disabled')!.addEventListener('click', () => {
      sectionDropdown.style.display = 'none';
      this._hideDisabled = !this._hideDisabled;
      this._refreshAccessIds();
    });

    this._bindAccessIds();
  }

  // ---- Org Name ----

  private _orgNameHTML(): string {
    if (this.isNew) {
      return `
        <div id="org-name-section">
          <input class="field-input org-name-input" id="org-name-input" type="text"
                 value="${this._orgName}" placeholder="Organization name" autofocus>
        </div>
      `;
    }
    if (this._orgNameEditing) {
      return `
        <div id="org-name-section">
          <div class="org-name-row">
            <input class="field-input org-name-input" id="org-name-input" type="text" value="${this._orgName}">
            <div class="access-card-header-actions">
              <button class="btn-done" id="org-name-save">Save</button>
              <button class="btn-card-action" id="org-name-cancel">Cancel</button>
            </div>
          </div>
          ${this._orgNameError ? `<p class="section-save-error">${this._orgNameError}</p>` : ''}
        </div>
      `;
    }
    return `
      <div id="org-name-section" class="org-name-row">
        <div class="org-name">${this._orgName}</div>
        ${this._canManageSettings ? `<button class="btn-card-action" id="org-name-edit">Edit</button>` : ''}
      </div>
    `;
  }

  private _refreshOrgName() {
    const el = this.root.querySelector<HTMLElement>('#org-name-section')!;
    el.outerHTML = this._orgNameHTML();
    this._bindOrgName();
  }

  private _bindOrgName() {
    const el = this.root.querySelector<HTMLElement>('#org-name-section');
    if (!el) return;

    el.querySelector('#org-name-edit')?.addEventListener('click', () => {
      this._orgNameSnapshot = this._orgName;
      this._orgNameEditing = true;
      this._refreshOrgName();
      this.root.querySelector<HTMLInputElement>('#org-name-input')?.focus();
    });

    el.querySelector('#org-name-save')?.addEventListener('click', async () => {
      const val = el.querySelector<HTMLInputElement>('#org-name-input')?.value.trim();
      if (!val) return;
      this._orgName = val;
      this._setSaving(true);
      try {
        await orgService.updateOrg(this.orgId, this._currentOrgReq());
        this._orgNameEditing = false;
        this._orgNameError = '';
        this._refreshOrgName();
      } catch (err) {
        this._orgNameError = err instanceof Error ? err.message : 'Save failed.';
        this._refreshOrgName();
      } finally {
        this._setSaving(false);
      }
    });

    el.querySelector('#org-name-cancel')?.addEventListener('click', () => {
      this._orgName = this._orgNameSnapshot;
      this._orgNameEditing = false;
      this._orgNameError = '';
      this._refreshOrgName();
    });
  }

  // ---- Address ----

  private _addressSectionHTML(): string {
    const a = this._orgAddress;
    if (this._orgAddressEditing) {
      return `
        <section class="section" id="address-section">
          <div class="section-header">
            <div class="section-label mono">Address</div>
            ${!this.isNew ? `<div class="access-card-header-actions">
              <button class="btn-done" id="address-save">Save</button>
              <button class="btn-card-action" id="address-cancel">Cancel</button>
            </div>` : ''}
          </div>
          ${this._addressError ? `<p class="section-save-error">${this._addressError}</p>` : ''}
          <div class="card">
            <div class="pref-col" style="gap:12px">
              <div class="field-group">
                <div class="field-label mono">Street</div>
                <input class="field-input" id="addr-street1" type="text" value="${a.street1}" placeholder="Street address">
              </div>
              <div class="field-group">
                <div class="field-label mono">Street 2</div>
                <input class="field-input" id="addr-street2" type="text" value="${a.street2 ?? ''}" placeholder="Apt, suite, etc. (optional)">
              </div>
              <div class="addr-row">
                <div class="field-group" style="flex:2">
                  <div class="field-label mono">City</div>
                  <input class="field-input" id="addr-city" type="text" value="${a.city}">
                </div>
                <div class="field-group" style="flex:1">
                  <div class="field-label mono">State</div>
                  <input class="field-input" id="addr-state" type="text" value="${a.state}" maxlength="2">
                </div>
                <div class="field-group" style="flex:1">
                  <div class="field-label mono">ZIP</div>
                  <input class="field-input" id="addr-postal" type="text" value="${a.postalCode}" inputmode="numeric">
                </div>
              </div>
            </div>
          </div>
        </section>
      `;
    }
    return `
      <section class="section" id="address-section">
        <div class="section-header">
          <div class="section-label mono">Address</div>
          ${this._canManageSettings ? `<button class="btn-card-action" id="address-edit">Edit</button>` : ''}
        </div>
        <div class="card">
          <div class="pref-col">
            <div class="address-val">
              ${a.street1}${a.street2 ? `<br>${a.street2}` : ''}
              <br>${a.city}, ${a.state} ${a.postalCode}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private _refreshAddressSection() {
    const el = this.root.querySelector<HTMLElement>('#address-section')!;
    el.outerHTML = this._addressSectionHTML();
    this._bindAddressSection();
  }

  private _bindAddressSection() {
    const el = this.root.querySelector<HTMLElement>('#address-section');
    if (!el) return;

    el.querySelector('#address-edit')?.addEventListener('click', () => {
      this._orgAddressSnapshot = { ...this._orgAddress };
      this._orgAddressEditing = true;
      this._refreshAddressSection();
    });

    el.querySelector('#address-save')?.addEventListener('click', async () => {
      const street1 = el.querySelector<HTMLInputElement>('#addr-street1')?.value.trim() ?? '';
      const street2 = el.querySelector<HTMLInputElement>('#addr-street2')?.value.trim() || undefined;
      const city = el.querySelector<HTMLInputElement>('#addr-city')?.value.trim() ?? '';
      const state = el.querySelector<HTMLInputElement>('#addr-state')?.value.trim() ?? '';
      const postalCode = el.querySelector<HTMLInputElement>('#addr-postal')?.value.trim() ?? '';
      this._orgAddress = { street1, street2, city, state, postalCode };
      this._setSaving(true);
      try {
        await orgService.updateOrg(this.orgId, this._currentOrgReq());
        this._orgAddressEditing = false;
        this._addressError = '';
        this._refreshAddressSection();
      } catch (err) {
        this._addressError = err instanceof Error ? err.message : 'Save failed.';
        this._refreshAddressSection();
      } finally {
        this._setSaving(false);
      }
    });

    el.querySelector('#address-cancel')?.addEventListener('click', () => {
      this._orgAddress = { ...this._orgAddressSnapshot };
      this._orgAddressEditing = false;
      this._addressError = '';
      this._refreshAddressSection();
    });
  }

  // ---- Skip Checkout Card ----

  private _skipCheckoutCardHTML(): string {
    if (this._skipCheckoutEditing) {
      return `
        <div class="card" id="skip-checkout-card">
          <div class="pref-row">
            <div class="pref-label">Skip checkout confirmations</div>
            <button class="toggle ${this.prefs.skipCheckoutConfirmations ? 'on' : ''}"
              id="pref-skip-checkout" role="switch"
              aria-checked="${this.prefs.skipCheckoutConfirmations}">
              <span class="toggle-thumb"></span>
            </button>
          </div>
          ${!this.isNew ? `
          ${this._skipCheckoutError ? `<div class="card-divider"></div><p class="section-save-error" style="padding:8px 16px">${this._skipCheckoutError}</p>` : ''}
          <div class="card-divider"></div>
          <div class="pref-row">
            <div style="flex:1"></div>
            <div class="access-card-header-actions">
              <button class="btn-done" id="skip-checkout-save">Save</button>
              <button class="btn-card-action" id="skip-checkout-cancel">Cancel</button>
            </div>
          </div>` : ''}
        </div>
      `;
    }
    const skipText = this.prefs.skipCheckoutConfirmations ? 'Enabled' : 'Disabled';
    const skipClass = this.prefs.skipCheckoutConfirmations ? 'badge-on' : 'badge-off';
    return `
      <div class="card" id="skip-checkout-card">
        <div class="pref-row">
          <div class="pref-label">Skip checkout confirmations</div>
          <div class="access-card-header-actions">
            <span class="status-badge ${skipClass}">${skipText}</span>
            ${this._canManageSettings ? `<button class="btn-card-action" id="skip-checkout-edit">Edit</button>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  private _refreshSkipCheckoutCard() {
    const card = this.root.querySelector<HTMLElement>('#skip-checkout-card')!;
    card.outerHTML = this._skipCheckoutCardHTML();
    this._bindSkipCheckoutCard();
  }

  private _bindSkipCheckoutCard() {
    const card = this.root.querySelector<HTMLElement>('#skip-checkout-card');
    if (!card) return;

    card.querySelector('#skip-checkout-edit')?.addEventListener('click', () => {
      this._skipCheckoutSnapshot = this.prefs.skipCheckoutConfirmations;
      this._skipCheckoutEditing = true;
      this._refreshSkipCheckoutCard();
    });

    card.querySelector('#pref-skip-checkout')?.addEventListener('click', () => {
      this.prefs.skipCheckoutConfirmations = !this.prefs.skipCheckoutConfirmations;
      const btn = card.querySelector<HTMLButtonElement>('#pref-skip-checkout')!;
      btn.classList.toggle('on', this.prefs.skipCheckoutConfirmations);
      btn.setAttribute('aria-checked', String(this.prefs.skipCheckoutConfirmations));
    });

    card.querySelector('#skip-checkout-save')?.addEventListener('click', async () => {
      this._setSaving(true);
      try {
        await orgService.updateOrg(this.orgId, this._currentOrgReq());
        this._skipCheckoutEditing = false;
        this._skipCheckoutError = '';
        this._refreshSkipCheckoutCard();
      } catch (err) {
        this._skipCheckoutError = err instanceof Error ? err.message : 'Save failed.';
        this._refreshSkipCheckoutCard();
      } finally {
        this._setSaving(false);
      }
    });

    card.querySelector('#skip-checkout-cancel')?.addEventListener('click', () => {
      this.prefs.skipCheckoutConfirmations = this._skipCheckoutSnapshot;
      this._skipCheckoutEditing = false;
      this._skipCheckoutError = '';
      this._refreshSkipCheckoutCard();
    });
  }

  // ---- Text Message Template Card ----

  private _templateCardHTML(): string {
    if (!this._templateVisible) return '';
    const template = this._template;
    if (this._templateEditing) {
      return `
        <div class="card" id="template-card">
          <div class="pref-row">
            <div class="pref-label">Text message template</div>
            ${!this.isNew ? `<div class="access-card-header-actions">
              <button class="btn-done" id="template-save">Save</button>
              <button class="btn-card-action" id="template-cancel">Cancel</button>
            </div>` : ''}
          </div>
          <div class="card-divider"></div>
          <div class="pref-col">
            <textarea class="field-textarea" id="pref-template" rows="3">${template}</textarea>
            ${this._templateError ? `<p class="section-save-error">${this._templateError}</p>` : ''}
          </div>
        </div>
      `;
    }
    return `
      <div class="card" id="template-card">
        <div class="pref-row">
          <div class="pref-label">Text message template</div>
          <div class="access-card-header-actions">
            ${this._canManageSettings ? `<button class="btn-card-action" id="template-edit">Edit</button>` : ''}
          </div>
        </div>
        <div class="card-divider"></div>
        <div class="pref-col">
          <div class="${template ? 'tp-preview' : 'tp-empty'}">${template || '&lt;not set&gt;'}</div>
        </div>
      </div>
    `;
  }

  private _refreshTemplateCard() {
    const card = this.root.querySelector<HTMLElement>('#template-card')!;
    card.outerHTML = this._templateCardHTML();
    this._bindTemplateCard();
  }

  private _bindTemplateCard() {
    const card = this.root.querySelector<HTMLElement>('#template-card');
    if (!card) return;

    card.querySelector('#template-edit')?.addEventListener('click', () => {
      this._templateSnapshot = this._template;
      this._templateEditing = true;
      this._refreshTemplateCard();
    });

    card.querySelector('#template-save')?.addEventListener('click', async () => {
      const ta = card.querySelector<HTMLTextAreaElement>('#pref-template');
      const value = ta?.value ?? this._template;
      this._setSaving(true);
      try {
        await ticketService.updateTextMessageTemplate(this.orgId, value);
        this._template = value;
        this._templateEditing = false;
        this._templateError = '';
        this._refreshTemplateCard();
      } catch (err) {
        this._templateError = err instanceof Error ? err.message : 'Save failed.';
        this._refreshTemplateCard();
      } finally {
        this._setSaving(false);
      }
    });

    card.querySelector('#template-cancel')?.addEventListener('click', () => {
      this._template = this._templateSnapshot;
      this._templateEditing = false;
      this._templateError = '';
      this._refreshTemplateCard();
    });
  }

  // ---- ACL ----

  private _aclHTML(): string {
    if (this.acl.length === 0) return '<p class="empty-msg mono">No members yet.</p>';
    return this.acl.map((e, i) => this._aclCardHTML(e, i)).join('');
  }

  private _aclCardHTML(e: OrgAclEntry, i: number): string {
    const editing = this._aclEditingIds.has(i);
    const saving = this._aclSaving.has(i);
    const isNew = this._aclNewIndices.has(i);
    const error = this._aclErrors.get(i);

    if (editing) {
      return `
        <div class="access-card access-card--editing">
          <div class="access-card-header">
            ${isNew
              ? `<input class="field-input acl-email-input" type="email" data-acl-action="email" data-index="${i}" value="${e.email}" placeholder="email@example.com">`
              : `<span class="acl-email-val">${e.email}</span>`}
            <div class="access-card-header-actions">
              ${saving ? `
                <button class="btn-done" disabled>Saving…</button>
              ` : `
                <button class="btn-done" data-acl-action="save" data-index="${i}">Save</button>
                <button class="btn-card-action" data-acl-action="cancel" data-index="${i}">Cancel</button>
                <button class="btn-icon-danger" data-acl-action="delete" data-index="${i}" aria-label="Delete">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                    <line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/>
                  </svg>
                </button>
              `}
            </div>
          </div>
          ${error ? `<div class="access-card-error">${error}</div>` : ''}
          <div class="access-card-body">
            <div class="field-group">
              <div class="field-label mono">Roles</div>
              <div class="check-row">
                ${(['MGR', 'CHECK-IN', 'CHECK-OUT'] as OrgRole[]).map(role => {
                  const label = role === 'MGR' ? 'Manager' : role === 'CHECK-IN' ? 'Check In' : 'Check Out';
                  return `<label class="check-label">
                    <input type="checkbox" data-acl-action="role" data-index="${i}" data-role="${role}" ${e.roles.includes(role) ? 'checked' : ''}>
                    <span>${label}</span>
                  </label>`;
                }).join('')}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    const roleLabels = e.roles.map(r => r === 'MGR' ? 'Manager' : r === 'CHECK-IN' ? 'Check In' : 'Check Out');
    return `
      <div class="access-card">
        <div class="access-card-header">
          <div class="access-card-title">
            <span class="acl-email-val" title="${e.email}">${e.email}</span>
            <div class="role-tags">
              ${roleLabels.length ? roleLabels.map(l => `<span class="role-tag">${l}</span>`).join('') : '<span class="view-empty">No roles</span>'}
            </div>
          </div>
          <div class="access-card-header-actions">
            ${this._canManageAcl ? `<button class="btn-card-action" data-acl-action="edit" data-index="${i}">Edit</button>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  private _refreshAcl() {
    const list = this.root.querySelector<HTMLElement>('#acl-list')!;
    list.innerHTML = this._aclHTML();
    this._bindAcl();
  }

  private _bindAcl() {
    this._aclAbort?.abort();
    this._aclAbort = new AbortController();
    const signal = this._aclAbort.signal;

    const list = this.root.querySelector<HTMLElement>('#acl-list');
    if (!list) return;

    list.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-acl-action]');
      if (!btn) return;
      const action = btn.dataset.aclAction!;
      const idx = Number(btn.dataset.index);

      if (action === 'edit') {
        this._aclSnapshots.set(idx, { ...this.acl[idx], roles: [...this.acl[idx].roles] });
        this._aclEditingIds.add(idx);
        this._aclErrors.delete(idx);
        this._refreshAcl();

      } else if (action === 'save') {
        const isNew = this._aclNewIndices.has(idx);
        const entry = this.acl[idx];
        if (isNew && !entry.email) return;

        this._aclSaving.add(idx);
        this._aclErrors.delete(idx);
        this._refreshAcl();

        const call = isNew
          ? orgService.createOrgAcl(this.orgId, entry.email, entry.roles)
          : orgService.updateOrgAcl(this.orgId, entry.aclId!, entry.roles);

        call.then(saved => {
          this.acl[idx] = saved;
          this._aclEditingIds.delete(idx);
          this._aclNewIndices.delete(idx);
          this._aclSnapshots.delete(idx);
          this._aclSaving.delete(idx);
          this._refreshAcl();
        }).catch(err => {
          this._aclSaving.delete(idx);
          this._aclErrors.set(idx, err instanceof Error ? err.message : 'Save failed.');
          this._refreshAcl();
        });

      } else if (action === 'cancel') {
        if (this._aclNewIndices.has(idx)) {
          this._aclNewIndices.delete(idx);
          this._aclEditingIds.delete(idx);
          this._aclErrors.delete(idx);
          const shifted = new Set<number>();
          for (const id of this._aclEditingIds) { shifted.add(id > idx ? id - 1 : id); }
          this._aclEditingIds = shifted;
          this.acl.splice(idx, 1);
        } else {
          const snap = this._aclSnapshots.get(idx);
          if (snap) { this.acl[idx] = snap; this._aclSnapshots.delete(idx); }
          this._aclEditingIds.delete(idx);
          this._aclErrors.delete(idx);
        }
        this._refreshAcl();

      } else if (action === 'delete') {
        const isNew = this._aclNewIndices.has(idx);
        if (isNew) {
          // unsaved entry — discard without API call
          const shift = (s: Set<number>) => {
            const n = new Set<number>();
            for (const id of s) { if (id !== idx) n.add(id > idx ? id - 1 : id); }
            return n;
          };
          this._aclEditingIds = shift(this._aclEditingIds);
          this._aclNewIndices = shift(this._aclNewIndices);
          this._aclSnapshots.delete(idx);
          this.acl.splice(idx, 1);
          this._refreshAcl();
        } else {
          const entry = this.acl[idx];
          if (!confirm(`Are you sure you want to delete ${entry.email}?`)) return;
          this._aclSaving.add(idx);
          this._aclErrors.delete(idx);
          this._refreshAcl();

          orgService.deleteOrgAcl(this.orgId, entry.aclId!).then(() => {
            this.acl.splice(idx, 1);
            const shift = (s: Set<number>) => {
              const n = new Set<number>();
              for (const id of s) { if (id !== idx) n.add(id > idx ? id - 1 : id); }
              return n;
            };
            this._aclEditingIds = shift(this._aclEditingIds);
            this._aclNewIndices = shift(this._aclNewIndices);
            this._aclSaving.delete(idx);
            const shiftedErrors = new Map<number, string>();
            for (const [k, v] of this._aclErrors) { if (k !== idx) shiftedErrors.set(k > idx ? k - 1 : k, v); }
            this._aclErrors = shiftedErrors;
            this._refreshAcl();
          }).catch(err => {
            this._aclSaving.delete(idx);
            this._aclErrors.set(idx, err instanceof Error ? err.message : 'Delete failed.');
            this._refreshAcl();
          });
        }
      }
    }, { signal });

    list.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      const action = input.dataset.aclAction;
      const idx = Number(input.dataset.index);
      if (action === 'role') {
        const role = input.dataset.role as OrgRole;
        if (input.checked) { if (!this.acl[idx].roles.includes(role)) this.acl[idx].roles.push(role); }
        else { this.acl[idx].roles = this.acl[idx].roles.filter(r => r !== role); }
      } else if (action === 'email') {
        this.acl[idx].email = input.value.trim();
      }
    }, { signal });

    list.addEventListener('keydown', (e) => {
      const input = e.target as HTMLInputElement;
      if ((e as KeyboardEvent).key === 'Enter' && input.dataset.aclAction === 'email') {
        const idx = Number(input.dataset.index);
        if (this.acl[idx].email) {
          list.querySelector<HTMLButtonElement>(`[data-acl-action="save"][data-index="${idx}"]`)?.click();
        }
      }
    }, { signal });
  }

  // ---- Access IDs ----

  private _accessIdsHTML(): string {
    if (this.accessIds.length === 0) {
      return '<p class="empty-msg mono">No access tokens yet.</p>';
    }
    const cards = this.accessIds
      .map((a, i) => ({ a, i }))
      .filter(({ a, i }) => !this._hideDisabled || !a.disabled || this._editingIds.has(i))
      .map(({ a, i }) => this._accessIdCardHTML(a, i))
      .join('');
    return cards || '<p class="empty-msg mono">All access tokens are hidden.</p>';
  }

  private _accessIdCardHTML(a: AccessId, i: number): string {
    const editing = this._editingIds.has(i);
    const saving = this._accessSaving.has(i);
    const isNew = this._newIndices.has(i);
    const error = this._accessErrors.get(i);
    const roleLabels = a.roles.map(r => r === 'CHECK-IN' ? 'Check In' : 'Check Out');

    if (editing) {
      return `
        <div class="access-card access-card--editing">
          <div class="access-card-header">
            <span class="access-id-val mono">${isNew ? 'New token' : (a.token ?? a.id)}</span>
            <div class="access-card-header-actions">
              ${saving ? `
                <button class="btn-done" disabled>Saving…</button>
              ` : `
                <button class="btn-done" data-action="save" data-index="${i}">Save</button>
                <button class="btn-card-action" data-action="cancel-edit" data-index="${i}">Cancel</button>
                ${!isNew ? `
                  <button class="btn-icon-danger" data-action="delete" data-index="${i}" aria-label="Delete">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                      <line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/>
                    </svg>
                  </button>
                ` : ''}
              `}
            </div>
          </div>
          ${error ? `<div class="access-card-error">${error}</div>` : ''}
          <div class="access-card-body">
            <div class="field-group">
              <div class="field-label mono">Name</div>
              <input class="field-input" type="text" placeholder="e.g. Front Door" data-action="name" data-index="${i}" value="${a.name ?? ''}">
            </div>
            <div class="card-divider"></div>
            <div class="pref-row">
              <div class="pref-label">Enabled</div>
              <button class="toggle ${a.disabled ? '' : 'on'}"
                data-action="toggle-disabled" data-index="${i}"
                role="switch" aria-checked="${!a.disabled}" aria-label="Enabled">
                <span class="toggle-thumb"></span>
              </button>
            </div>
            <div class="card-divider"></div>
            <div class="field-group">
              <div class="field-label mono">Roles</div>
              <div class="check-row">
                <label class="check-label">
                  <input type="checkbox" data-action="role" data-role="CHECK-IN" data-index="${i}" ${a.roles.includes('CHECK-IN') ? 'checked' : ''}>
                  <span>Check In</span>
                </label>
                <label class="check-label">
                  <input type="checkbox" data-action="role" data-role="CHECK-OUT" data-index="${i}" ${a.roles.includes('CHECK-OUT') ? 'checked' : ''}>
                  <span>Check Out</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="access-card">
        <div class="access-card-header">
          <div class="access-card-title">
            ${a.name ? `<span class="access-card-name">${a.name}</span>` : ''}
            <span class="access-id-val mono">${a.token ?? a.id}</span>
          </div>
          <div class="access-card-header-actions">
            <a class="btn-card-action btn-card-link" href="/app/scan/${encodeURIComponent(a.token ?? a.id)}" target="_blank" rel="noopener noreferrer">↗</a>
            ${this._canManageSettings ? `<button class="btn-card-action" data-action="edit" data-index="${i}">Edit</button>` : ''}
          </div>
        </div>
        <div class="access-card-body">
          <div class="view-row">
            <span class="view-label mono">Enabled</span>
            <span class="status-badge ${a.disabled ? 'badge-off' : 'badge-on'}">${a.disabled ? 'Disabled' : 'Enabled'}</span>
          </div>
          <div class="view-row">
            <span class="view-label mono">Roles</span>
            <div class="role-tags">
              ${roleLabels.length ? roleLabels.map(l => `<span class="role-tag">${l}</span>`).join('') : '<span class="view-empty">None</span>'}
            </div>
          </div>
          ${a.createdTS ? `
          <div class="view-row">
            <span class="view-label mono">Created</span>
            <span class="view-date">${new Date(a.createdTS).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
          </div>` : ''}
        </div>
      </div>
    `;
  }

  private _refreshAccessIds() {
    const list = this.root.querySelector<HTMLElement>('#access-ids-list')!;
    list.innerHTML = this._accessIdsHTML();
    const toggleBtn = this.root.querySelector<HTMLElement>('#toggle-hide-disabled');
    if (toggleBtn) toggleBtn.textContent = this._hideDisabled ? 'Show Disabled' : 'Hide Disabled';
    this._bindAccessIds();
  }

  private _bindAccessIds() {
    this._accessIdsAbort?.abort();
    this._accessIdsAbort = new AbortController();
    const signal = this._accessIdsAbort.signal;

    const list = this.root.querySelector<HTMLElement>('#access-ids-list');
    if (!list) return;

    list.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action!;
      const idx = Number(btn.dataset.index);

      if (action === 'edit') {
        const a = this.accessIds[idx];
        this._snapshots.set(idx, { ...a, roles: [...a.roles] });
        this._editingIds.add(idx);
        this._accessErrors.delete(idx);
        this._refreshAccessIds();

      } else if (action === 'save') {
        const isNew = this._newIndices.has(idx);
        const entry = this.accessIds[idx];
        this._accessSaving.add(idx);
        this._accessErrors.delete(idx);
        this._refreshAccessIds();

        const req = { name: entry.name, roles: entry.roles, disabled: entry.disabled };
        const call = isNew
          ? orgService.createOrgAnonAccess(this.orgId, req)
          : orgService.updateOrgAnonAccess(this.orgId, entry.id, req);

        call.then(saved => {
          this.accessIds[idx] = saved;
          this._editingIds.delete(idx);
          this._newIndices.delete(idx);
          this._snapshots.delete(idx);
          this._accessSaving.delete(idx);
          this._refreshAccessIds();
        }).catch(err => {
          this._accessSaving.delete(idx);
          this._accessErrors.set(idx, err instanceof Error ? err.message : 'Save failed.');
          this._refreshAccessIds();
        });

      } else if (action === 'cancel-edit') {
        if (this._newIndices.has(idx)) {
          this._newIndices.delete(idx);
          this._editingIds.delete(idx);
          this._accessErrors.delete(idx);
          const shifted = new Set<number>();
          for (const id of this._editingIds) { shifted.add(id > idx ? id - 1 : id); }
          this._editingIds = shifted;
          this.accessIds.splice(idx, 1);
        } else {
          const snap = this._snapshots.get(idx);
          if (snap) { this.accessIds[idx] = snap; this._snapshots.delete(idx); }
          this._editingIds.delete(idx);
          this._accessErrors.delete(idx);
        }
        this._refreshAccessIds();

      } else if (action === 'delete') {
        const entry = this.accessIds[idx];
        const label = entry.name || entry.token || entry.id;
        if (!confirm(`Are you sure you want to delete ${label}?`)) return;
        this._accessSaving.add(idx);
        this._accessErrors.delete(idx);
        this._refreshAccessIds();

        orgService.deleteOrgAnonAccess(this.orgId, entry.id).then(() => {
          this.accessIds.splice(idx, 1);
          const shift = (s: Set<number>) => {
            const n = new Set<number>();
            for (const id of s) { if (id !== idx) n.add(id > idx ? id - 1 : id); }
            return n;
          };
          this._editingIds = shift(this._editingIds);
          this._newIndices = shift(this._newIndices);
          this._accessSaving.delete(idx);
          const shiftedErrors = new Map<number, string>();
          for (const [k, v] of this._accessErrors) { if (k !== idx) shiftedErrors.set(k > idx ? k - 1 : k, v); }
          this._accessErrors = shiftedErrors;
          this._refreshAccessIds();
        }).catch(err => {
          this._accessSaving.delete(idx);
          this._accessErrors.set(idx, err instanceof Error ? err.message : 'Delete failed.');
          this._refreshAccessIds();
        });

      } else if (action === 'toggle-disabled') {
        this.accessIds[idx].disabled = !this.accessIds[idx].disabled;
        const isOn = !this.accessIds[idx].disabled;
        btn.classList.toggle('on', isOn);
        btn.setAttribute('aria-checked', String(isOn));
      }
    }, { signal });

    list.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      const action = input.dataset.action;
      const idx = Number(input.dataset.index);
      if (action === 'name') {
        this.accessIds[idx].name = input.value.trim() || undefined;
      } else if (action === 'role') {
        const role = input.dataset.role as AccessRole;
        const roles = this.accessIds[idx].roles;
        if (input.checked) { if (!roles.includes(role)) roles.push(role); }
        else { this.accessIds[idx].roles = roles.filter(r => r !== role); }
      }
    }, { signal });
  }

  // ---- New Org ----

  private _bindNewMode() {
    const createBtn = this.root.querySelector<HTMLButtonElement>('#create-org-btn')!;
    const errorEl = this.root.querySelector<HTMLElement>('#new-org-error')!;

    createBtn.addEventListener('click', async () => {
      const name = this.root.querySelector<HTMLInputElement>('#org-name-input')?.value.trim() ?? '';
      if (!name) {
        this.root.querySelector<HTMLInputElement>('#org-name-input')?.focus();
        return;
      }

      const storeNumber = this.root.querySelector<HTMLInputElement>('#store-number-input')?.value.trim() || undefined;
      const street1 = this.root.querySelector<HTMLInputElement>('#addr-street1')?.value.trim() ?? '';
      const street2 = this.root.querySelector<HTMLInputElement>('#addr-street2')?.value.trim() || undefined;
      const city = this.root.querySelector<HTMLInputElement>('#addr-city')?.value.trim() ?? '';
      const state = this.root.querySelector<HTMLInputElement>('#addr-state')?.value.trim() ?? '';
      const postalCode = this.root.querySelector<HTMLInputElement>('#addr-postal')?.value.trim() ?? '';
      createBtn.disabled = true;
      createBtn.textContent = 'Creating…';
      errorEl.textContent = '';

      try {
        const org = await orgService.createOrg({
          name,
          storeNumber,
          address: { street1, street2, city, state, postalCode },
          prefs: {
            skipCheckoutConfirmations: this.prefs.skipCheckoutConfirmations,
          },
        });
        navigate(`/app/org/${org.id}`);
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : 'Failed to create organization.';
        createBtn.disabled = false;
        createBtn.textContent = 'Create Organization';
      }
    });

    this.root.querySelector('#cancel-org-btn')!.addEventListener('click', () => navigate('/app'));
  }

  // ---- QR Code Print ----

  private _printQrCodes() {
    navigate(`/app/org/${this.orgId}/qr-print`);
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper, #E4DED2);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
        line-height: 1.55;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      /* header */
      .top-bar {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(228,222,210,0.92);
        backdrop-filter: blur(6px);
        position: sticky;
        top: 0;
        z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700;
        font-size: 18px;
        letter-spacing: -0.02em;
        display: flex;
        align-items: center;
        gap: 7px;
      }
      .dot {
        width: 8px; height: 8px;
        border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block;
        flex-shrink: 0;
      }
      .header-actions { display: flex; align-items: center; gap: 10px; }
      .btn-back {
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 5px 12px;
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer;
        transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }
      .menu-btn {
        display: flex; align-items: center; justify-content: center;
        width: 28px; height: 28px;
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer; padding: 0;
        transition: background .15s;
      }
      .menu-btn:hover { background: var(--paper-light, #FAF7F0); }

      /* hero */
      .org-hero {
        width: 100%; height: 140px;
        background: var(--carbon, #6B5B95);
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .org-initial {
        font-family: 'Space Grotesk', sans-serif;
        font-size: 72px; font-weight: 700;
        color: rgba(250,247,240,0.45); line-height: 1;
      }

      /* body */
      .body {
        width: 100%; max-width: 520px;
        padding: 24px 20px 48px;
        display: flex; flex-direction: column; gap: 28px;
      }
      .org-name-row {
        display: flex; align-items: center;
        justify-content: space-between; gap: 12px;
      }
      .org-name {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 24px;
        letter-spacing: -0.02em; line-height: 1.2;
      }
      .org-name-input {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 22px;
        letter-spacing: -0.02em; flex: 1;
      }
      .addr-row { display: flex; gap: 8px; }
      .addr-row > .field-group { min-width: 0; }
      .store-num { font-size: 13px; color: var(--ink-soft, #5A5346); margin-top: -20px; }

      /* sections */
      .section { display: flex; flex-direction: column; gap: 10px; }
      .section-header { display: flex; align-items: center; justify-content: space-between; }
      .section-label {
        font-size: 11px; letter-spacing: .08em;
        text-transform: uppercase; color: var(--ink-soft, #5A5346);
      }

      /* card */
      .card {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        overflow: hidden;
      }
      .card-divider { height: 1px; background: var(--line, #C9C0AC); }
      .pref-row {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 16px;
      }
      .pref-col { padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; }
      .pref-label { font-size: 14px; color: var(--ink, #2A2622); }

      /* toggle switch */
      .toggle {
        width: 42px; height: 24px;
        background: var(--line, #C9C0AC);
        border: none; border-radius: 12px;
        cursor: pointer; padding: 2px;
        display: flex; align-items: center;
        transition: background .2s;
        flex-shrink: 0;
      }
      .toggle.on { background: var(--kiln, #C97A2E); }
      .toggle-thumb {
        width: 20px; height: 20px;
        background: var(--paper-light, #FAF7F0);
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,.2);
        transition: transform .2s;
      }
      .toggle.on .toggle-thumb { transform: translateX(18px); }

      /* textarea */
      .field-textarea {
        width: 100%;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 10px 12px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 13px;
        color: var(--ink, #2A2622);
        resize: vertical;
        line-height: 1.6;
      }
      .field-textarea:focus {
        outline: 2px solid var(--kiln, #C97A2E);
        outline-offset: 1px; border-color: transparent;
      }

      .section-menu-wrap { position: relative; }
      .section-dropdown {
        position: absolute; top: calc(100% + 6px); right: 0;
        min-width: 180px;
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(42,38,34,.12);
        overflow: hidden; z-index: 20;
      }
      .section-dropdown-item {
        display: block; width: 100%; padding: 10px 16px;
        background: transparent; border: none;
        font-family: 'Inter', sans-serif; font-size: 14px;
        color: var(--ink-soft, #5A5346); text-align: left;
        cursor: pointer; transition: background .12s;
      }
      .section-dropdown-item:hover { background: var(--paper, #E4DED2); }
      .section-dropdown-divider { height: 1px; background: var(--line, #C9C0AC); margin: 4px 0; }

      /* create button */
      .btn-create {
        background: transparent;
        border: 1px solid var(--kiln, #C97A2E);
        border-radius: 5px;
        padding: 5px 12px;
        font-family: 'Inter', sans-serif;
        font-size: 13px; font-weight: 600;
        color: var(--kiln, #C97A2E);
        cursor: pointer; transition: background .15s;
      }
      .btn-create:hover { background: rgba(201,122,46,.08); }

      /* access ID cards */
      .access-card {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        overflow: hidden;
        margin-bottom: 10px;
      }
      .access-card:last-child { margin-bottom: 0; }
      .access-card-header {
        display: flex; align-items: center; justify-content: space-between;
        gap: 12px;
        padding: 12px 14px;
        background: var(--paper-light, #FAF7F0);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .access-card-header-actions {
        display: flex; align-items: center; gap: 8px;
      }
      .access-card-title { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .access-card-name { font-size: 14px; font-weight: 600; color: var(--ink, #2A2622); }
      .access-id-val { font-size: 12px; color: var(--ink-soft, #5A5346); letter-spacing: .03em; }
      .access-card-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 14px; }

      /* view mode rows */
      .view-row { display: flex; align-items: center; gap: 12px; }
      .view-label { font-size: 11px; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-soft, #5A5346); min-width: 60px; }
      .view-empty { font-size: 13px; color: var(--ink-soft, #5A5346); font-style: italic; }
      .view-date { font-family: 'IBM Plex Mono', monospace; font-size: 12px; color: var(--ink-soft, #5A5346); }
      .status-badge {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 600;
        font-family: 'Inter', sans-serif;
      }
      .badge-on  { background: rgba(110,127,92,.15); color: var(--glaze, #6E7F5C); }
      .badge-off { background: rgba(90,83,70,.1);    color: var(--ink-soft, #5A5346); }
      .role-tags { display: flex; gap: 6px; flex-wrap: wrap; }
      .role-tag {
        display: inline-block;
        padding: 2px 8px;
        background: rgba(201,122,46,.12);
        color: var(--kiln-dark, #A15F1F);
        border-radius: 10px;
        font-size: 12px;
        font-family: 'IBM Plex Mono', monospace;
      }

      /* card action buttons */
      .btn-card-action {
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 4px;
        padding: 3px 10px;
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer;
        transition: background .12s;
      }
      .btn-card-action:hover { background: var(--paper, #E4DED2); }
      .btn-card-link { text-decoration: none; display: inline-flex; align-items: center; }
      .btn-done {
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none;
        border-radius: 4px;
        padding: 3px 10px;
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: background .15s;
      }
      .btn-done:hover { background: var(--kiln-dark, #A15F1F); }

      .field-group { display: flex; flex-direction: column; gap: 6px; }
      .field-label {
        font-size: 11px; letter-spacing: .07em;
        text-transform: uppercase; color: var(--ink-soft, #5A5346);
      }
      .check-row { display: flex; gap: 20px; }
      .check-label {
        display: flex; align-items: center; gap: 6px;
        font-size: 14px; color: var(--ink, #2A2622);
        cursor: pointer;
      }
      .check-label input { accent-color: var(--kiln, #C97A2E); cursor: pointer; }

      /* emails */
      .email-row {
        display: flex; align-items: center; justify-content: space-between;
        padding: 6px 0;
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .email-row:last-of-type { border-bottom: none; }
      .email-val { font-family: 'IBM Plex Mono', monospace; font-size: 13px; color: var(--ink, #2A2622); }
      .email-add-row { display: flex; gap: 8px; margin-top: 6px; }
      .field-input {
        flex: 1;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 8px 12px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 13px; color: var(--ink, #2A2622);
      }
      .field-input:focus {
        outline: 2px solid var(--kiln, #C97A2E);
        outline-offset: 1px; border-color: transparent;
      }
      .btn-add-email {
        padding: 8px 14px;
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 5px;
        font-family: 'Inter', sans-serif;
        font-size: 13px; font-weight: 600;
        cursor: pointer; white-space: nowrap;
        transition: background .15s;
      }
      .btn-add-email:hover { background: var(--kiln-dark, #A15F1F); }

      /* danger icon button */
      .btn-icon-danger {
        display: flex; align-items: center; justify-content: center;
        width: 26px; height: 26px;
        background: transparent;
        border: 1px solid #c0392b;
        border-radius: 4px;
        color: #c0392b;
        cursor: pointer; padding: 0; flex-shrink: 0;
        transition: background .15s;
      }
      .btn-icon-danger:hover { background: rgba(192,57,43,.08); }

      .tp-preview {
        font-size: 14px;
        color: var(--ink-soft, #5A5346);
        white-space: pre-wrap;
        line-height: 1.6;
      }
      .tp-empty {
        font-size: 13px;
        color: var(--line, #C9C0AC);
        font-style: italic;
      }
      .address-val {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 13px;
        color: var(--ink, #2A2622);
        line-height: 1.7;
      }
      /* ACL */
      .acl-email-val { font-family: 'IBM Plex Mono', monospace; font-size: 13px; color: var(--ink, #2A2622); word-break: break-all; }
      .acl-email-input { font-family: 'IBM Plex Mono', monospace; flex: 1; min-width: 0; }

      .empty-msg { font-size: 13px; color: var(--ink-soft, #5A5346); margin: 0; }
      .not-found { font-size: 15px; color: var(--ink-soft, #5A5346); text-align: center; padding: 48px 0; }
      .loading-msg { font-size: 13px; color: var(--ink-soft, #5A5346); text-align: center; padding: 48px 0; }

      #page-overlay {
        position: fixed; inset: 0; z-index: 100;
        background: rgba(228,222,210,0.65);
        backdrop-filter: blur(2px);
        display: flex; align-items: center; justify-content: center;
      }
      .spinner {
        width: 36px; height: 36px;
        border: 3px solid var(--line, #C9C0AC);
        border-top-color: var(--kiln, #C97A2E);
        border-radius: 50%;
        animation: spin 0.65s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      .section-save-error { font-size: 12px; color: #c0392b; margin: 4px 0 0; }

      .new-org-actions {
        display: flex; align-items: center; gap: 12px;
        padding-top: 8px;
      }
      .btn-create-org { padding: 10px 24px; font-size: 14px; }
      .field-optional { font-size: 10px; color: var(--ink-soft, #5A5346); font-style: italic; text-transform: none; letter-spacing: 0; }
      .new-org-error { font-size: 13px; color: #c0392b; margin: 0; min-height: 1em; }
      .access-card-error { padding: 6px 14px; font-size: 12px; color: #c0392b; background: rgba(192,57,43,.06); border-bottom: 1px solid var(--line, #C9C0AC); }

    </style>`;
  }
}

customElements.define('nu-org', NuOrg);